import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatNumber } from '../../../components/design-system/admin';
import {
    ContentPack,
    ContentPackItemKind,
    PackCandidate,
    PackEstimate,
    useContentPacksApi,
} from '../../../common/hooks/use-content-packs-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { KindPicker, normaliseKinds } from './kind-picker';

export const MAX_PRODUCTS = 12;

/**
 * Choose products and what to make; see what it costs before anything runs.
 * The cost is the API's own estimate (the Studio's prices), with the balance.
 */
export const MakePackDialog: React.FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    defaultKinds: ContentPackItemKind[];
    defaultPosts: number;
    preselect?: string[];
    onMade: (packs: ContentPack[]) => void;
}> = ({ open, onOpenChange, defaultKinds, defaultPosts, preselect, onMade }) => {
    const { t } = useTranslation();
    const api = useContentPacksApi();
    const [search, setSearch] = useState('');
    const [products, setProducts] = useState<PackCandidate[] | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [kinds, setKinds] = useState<ContentPackItemKind[]>(defaultKinds);
    const [posts, setPosts] = useState(defaultPosts);
    const [estimate, setEstimate] = useState<PackEstimate | null>(null);
    const [saving, setSaving] = useState(false);

    // Fresh every time it opens: the settings may have changed since.
    useEffect(() => {
        if (!open) return;
        setKinds(defaultKinds);
        setPosts(defaultPosts);
        setSelected(preselect?.length ? preselect.slice(0, MAX_PRODUCTS) : []);
        setSearch('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        let live = true;
        const timer = setTimeout(() => {
            api.candidates(search || undefined, 40)
                .then((rows) => live && setProducts(rows))
                .catch(() => live && setProducts([]));
        }, search ? 250 : 0);
        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [open, search, api]);

    useEffect(() => {
        if (!open) return undefined;
        let live = true;
        api.estimate({ productUuids: selected.length ? selected : undefined, kinds, postsPerPack: posts })
            .then((e) => live && setEstimate(e))
            .catch(() => live && setEstimate(null));
        return () => {
            live = false;
        };
    }, [open, selected, kinds, posts, api]);

    const toggle = (uuid: string) =>
        setSelected((s) => (s.includes(uuid) ? s.filter((u) => u !== uuid) : s.length >= MAX_PRODUCTS ? s : [...s, uuid]));

    const count = selected.length;
    const notEnough = !!estimate && count > 0 && estimate.enough === false;
    const effectiveKinds = useMemo(() => normaliseKinds(kinds), [kinds]);

    const submit = async () => {
        setSaving(true);
        try {
            const res = await api.create({ productUuids: selected, kinds: effectiveKinds, postsPerPack: posts });
            toast.success(t('CONTENT_PACKS.DIALOG.STARTED'));
            onMade(res.packs);
            onOpenChange(false);
        } catch (e) {
            const body = await readApiError(e);
            toast.error(body?.message ? String(body.message) : t('CONTENT_PACKS.DIALOG.FAILED'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="gap-5 sm:max-w-[640px]" data-testid="make-pack-dialog">
                <DialogHeader>
                    <DialogTitle>{t('CONTENT_PACKS.DIALOG.TITLE')}</DialogTitle>
                    <DialogDescription>{t('CONTENT_PACKS.DIALOG.DESC')}</DialogDescription>
                </DialogHeader>

                <section className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="text-xs font-medium text-muted-foreground">{t('CONTENT_PACKS.DIALOG.PRODUCTS')}</h3>
                        <span className="text-xs text-muted-foreground">
                            {count ? t('CONTENT_PACKS.DIALOG.SELECTED', { n: count }) : t('CONTENT_PACKS.DIALOG.MAX', { n: MAX_PRODUCTS })}
                        </span>
                    </div>
                    <div className="relative">
                        <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('CONTENT_PACKS.DIALOG.SEARCH')} className="ps-8" />
                    </div>
                    <div className="max-h-[260px] overflow-y-auto rounded-lg border">
                        {products === null && (
                            <p className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                                <Spinner className="size-3.5" />
                                {t('CONTENT_PACKS.DIALOG.LOADING')}
                            </p>
                        )}
                        {products?.length === 0 && <p className="p-3 text-xs text-muted-foreground">{t('CONTENT_PACKS.DIALOG.NONE')}</p>}
                        {products?.map((p) => {
                            const on = selected.includes(p.uuid);
                            return (
                                <label
                                    key={p.uuid}
                                    className={cn(
                                        'flex cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted/50',
                                        !p.canPack && 'cursor-not-allowed opacity-60',
                                    )}
                                >
                                    <Checkbox checked={on} disabled={!p.canPack || (!on && count >= MAX_PRODUCTS)} onCheckedChange={() => toggle(p.uuid)} />
                                    {p.thumbnailUrl ? (
                                        <img src={p.thumbnailUrl} alt="" className="size-8 shrink-0 rounded-md border object-cover" />
                                    ) : (
                                        <span className="size-8 shrink-0 rounded-md border bg-muted" />
                                    )}
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm">{p.name}</span>
                                        <span className="block truncate text-xs text-muted-foreground">{[p.sku, p.productType].filter(Boolean).join(' · ')}</span>
                                    </span>
                                    {!p.canPack && <span className="shrink-0 text-xs text-muted-foreground">{t('CONTENT_PACKS.DIALOG.NO_PICTURE')}</span>}
                                    {p.canPack && p.lastPack && <span className="shrink-0 text-xs text-muted-foreground">{t('CONTENT_PACKS.DIALOG.HAS_PACK')}</span>}
                                </label>
                            );
                        })}
                    </div>
                </section>

                <section className="flex flex-col gap-2">
                    <h3 className="text-xs font-medium text-muted-foreground">{t('CONTENT_PACKS.DIALOG.WHAT')}</h3>
                    <KindPicker kinds={kinds} onKinds={setKinds} posts={posts} onPosts={setPosts} prices={estimate?.perKind} />
                </section>

                <div className="rounded-lg bg-muted/60 p-3 text-sm" data-testid="pack-cost">
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="text-muted-foreground">
                            {estimate ? t('CONTENT_PACKS.DIALOG.COST', { packs: Math.max(1, count), per: formatNumber(estimate.perPack) }) : '—'}
                        </span>
                        <span className="font-medium tabular-nums">
                            {estimate ? t('CONTENT_PACKS.DIALOG.TOTAL', { n: formatNumber(estimate.perPack * Math.max(1, count)) }) : ''}
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {effectiveKinds.includes('post') && `${t('CONTENT_PACKS.DIALOG.POSTS_FREE')} `}
                        {estimate?.balance != null && t('CONTENT_PACKS.DIALOG.BALANCE', { n: formatNumber(estimate.balance) })}
                    </p>
                    {notEnough && <p className="mt-1 text-xs text-destructive">{t('CONTENT_PACKS.DIALOG.NOT_ENOUGH')}</p>}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('CONTENT_PACKS.DIALOG.CANCEL')}
                    </Button>
                    <Button onClick={submit} disabled={!count || saving || notEnough} data-testid="make-pack-submit">
                        {saving && <Spinner data-icon="inline-start" />}
                        {t('CONTENT_PACKS.DIALOG.SUBMIT', { count: Math.max(1, count) })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
